import React from "react";
import { Box, Paper, Typography, IconButton, Tooltip, Divider } from "@mui/material";
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { YieldMetric } from './YieldLab';
import { ExcludedPathEntry, buildExcludedTooltip } from '../../services/yieldCalculator';
import { formatCurrency } from '../../utils/namesFormater';

interface YieldValues {
  yield6M: number;
  yield12M: number;
  yield3Y: number;
  yield5Y: number;
  totalAmount: number;
  missing6M?: number;
  missing12M?: number;
  missing3Y?: number;
  missing5Y?: number;
  excluded6M?: ExcludedPathEntry[];
  excluded12M?: ExcludedPathEntry[];
  excluded3Y?: ExcludedPathEntry[];
  excluded5Y?: ExcludedPathEntry[];
}

interface Props {
  yields: YieldValues;
  originalYields?: YieldValues;
  neutralYields?: YieldValues;
  isModifiedView?: boolean;
  onMetricClick?: (metric: YieldMetric) => void;
  activeMetric?: YieldMetric | null;
}

interface MetricRowProps {
  label: string;
  value: number;
  originalValue?: number;
  neutralValue?: number;
  totalAmount?: number;
  originalTotalAmount?: number;
  neutralTotalAmount?: number;
  color: string;
  metric: YieldMetric;
  activeMetric?: YieldMetric | null;
  onMetricClick?: (metric: YieldMetric) => void;
  showDiff?: boolean;
  excludedPaths?: ExcludedPathEntry[];
  originalExcludedPaths?: ExcludedPathEntry[];
}


const WarningTooltip: React.FC<{ items: ExcludedPathEntry[] }> = ({ items }) =>
  items.length === 0 ? null : (
    <Tooltip
      title={<span style={{ whiteSpace: 'pre-line', direction: 'rtl', display: 'block', textAlign: 'right' }}>{buildExcludedTooltip(items)}</span>}
      arrow
    >
      <WarningAmberIcon color="warning" sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />
    </Tooltip>
  );

const MetricRow: React.FC<MetricRowProps> = ({ label, value, originalValue, neutralValue, totalAmount, originalTotalAmount, neutralTotalAmount, color, metric, activeMetric, onMetricClick, showDiff, excludedPaths, originalExcludedPaths }) => {
  const isActive = activeMetric === metric;
  const hasChange = showDiff && originalValue !== undefined && Math.abs(value - originalValue) > 0.001;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1 }}>
      <Tooltip title={`הצג ${label} בגרף`}>
        <IconButton
          onClick={() => onMetricClick?.(metric)}
          size="small"
          sx={{
            mt: 2.5,
            border: isActive ? `2px solid ${color}` : '1px solid rgba(0,0,0,0.23)',
            borderRadius: '8px',
            color: isActive ? color : 'action.active',
            backgroundColor: isActive ? `${color}14` : undefined,
            '&:hover': { backgroundColor: `${color}22` },
          }}
        >
          <ShowChartIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        {hasChange ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Tooltip title={`לפני שינוי${originalTotalAmount !== undefined ? ` | סכום: ${formatCurrency(originalTotalAmount)}` : ''}`} arrow>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {originalValue!.toFixed(2)}%
                </Typography>
              </Tooltip>
              <WarningTooltip items={originalExcludedPaths ?? []} />
            </Box>
            {neutralValue !== undefined && Math.abs(neutralValue - originalValue!) > 0.001 && (
              <>
                <ArrowBackIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Tooltip title={`אחרי שינוי מסלולים (ללא כספים חדשים)${neutralTotalAmount !== undefined ? ` | סכום: ${formatCurrency(neutralTotalAmount)}` : ''}`} arrow>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: neutralValue > originalValue! ? '#2e7d32' : '#e65100' }}>
                    {neutralValue.toFixed(2)}%
                  </Typography>
                </Tooltip>
              </>
            )}
            <ArrowBackIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Tooltip title={`אחרי שינוי כולל כספים חדשים${totalAmount !== undefined ? ` | סכום: ${formatCurrency(totalAmount)}` : ''}`} arrow>
                <Typography variant="h5" sx={{ fontWeight: 600, color: value > originalValue! ? '#2e7d32' : '#e65100' }}>
                  {value.toFixed(2)}%
                </Typography>
              </Tooltip>
              <WarningTooltip items={excludedPaths ?? []} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <Tooltip title={totalAmount !== undefined ? `סכום: ${formatCurrency(totalAmount)}` : ''} arrow>
              <Typography variant="h5" sx={{ fontWeight: 600, color }}>
                {value.toFixed(2)}%
              </Typography>
            </Tooltip>
            <WarningTooltip items={excludedPaths ?? []} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const YieldSummaryControl: React.FC<Props> = ({ yields, originalYields, neutralYields, isModifiedView, onMetricClick, activeMetric }) => {
  return (
    <Paper sx={{ p: 2, height: '100%', backgroundColor: isModifiedView ? '#fffbe6' : undefined }}>
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'right' }}>
        תשואות משוקללות
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <MetricRow
          label="תשואה 6M"
          value={yields.yield6M}
          originalValue={originalYields?.yield6M}
          neutralValue={neutralYields?.yield6M}
          totalAmount={yields.totalAmount}
          originalTotalAmount={originalYields?.totalAmount}
          neutralTotalAmount={neutralYields?.totalAmount}
          color="#1565c0"
          metric="cumulative_6m_yield"
          activeMetric={activeMetric}
          onMetricClick={onMetricClick}
          showDiff={isModifiedView}
          excludedPaths={yields.excluded6M}
          originalExcludedPaths={originalYields?.excluded6M}
        />
        <MetricRow
          label="תשואה 12M"
          value={yields.yield12M}
          originalValue={originalYields?.yield12M}
          neutralValue={neutralYields?.yield12M}
          totalAmount={yields.totalAmount}
          originalTotalAmount={originalYields?.totalAmount}
          neutralTotalAmount={neutralYields?.totalAmount}
          color="#1e88e5"
          metric="cumulative_12m_yield"
          activeMetric={activeMetric}
          onMetricClick={onMetricClick}
          showDiff={isModifiedView}
          excludedPaths={yields.excluded12M}
          originalExcludedPaths={originalYields?.excluded12M}
        />

        <Divider />

        <MetricRow
          label="תשואה שנתית 3Y"
          value={yields.yield3Y}
          originalValue={originalYields?.yield3Y}
          neutralValue={neutralYields?.yield3Y}
          totalAmount={yields.totalAmount}
          originalTotalAmount={originalYields?.totalAmount}
          neutralTotalAmount={neutralYields?.totalAmount}
          color="#2e7d32"
          metric="avg_annual_yield_trailing_3yrs"
          activeMetric={activeMetric}
          onMetricClick={onMetricClick}
          showDiff={isModifiedView}
          excludedPaths={yields.excluded3Y}
          originalExcludedPaths={originalYields?.excluded3Y}
        />
        <MetricRow
          label="תשואה שנתית 5Y"
          value={yields.yield5Y}
          originalValue={originalYields?.yield5Y}
          neutralValue={neutralYields?.yield5Y}
          totalAmount={yields.totalAmount}
          originalTotalAmount={originalYields?.totalAmount}
          neutralTotalAmount={neutralYields?.totalAmount}
          color="#e65100"
          metric="avg_annual_yield_trailing_5yrs"
          activeMetric={activeMetric}
          onMetricClick={onMetricClick}
          showDiff={isModifiedView}
          excludedPaths={yields.excluded5Y}
          originalExcludedPaths={originalYields?.excluded5Y}
        />
      </Box>
    </Paper>
  );
};

export default YieldSummaryControl;
